/**
 * The character n-gram matching of 3ngram, computed live from the real strings.
 * Same method as the engine's `trigrams()` / `trigramSimilarity()`: the text is
 * lowercased and padded with n - 1 spaces on each side, every window of n characters
 * is a gram, a text is the set of its grams, and two texts are compared with the
 * Jaccard index |A ∩ B| / |A ∪ B|. Nothing here is hard-coded: change the strings and
 * every count, highlight and score follows.
 */

/** One window of the scan: the gram and the index of its first character in `padded`. */
export type GramWindow = { gram: string; start: number };

export type NgramScan = {
	/** The text as the engine reads it: lowercased, padded with n - 1 spaces per side. */
	padded: string;
	/** Every window in reading order, repeats included. */
	windows: GramWindow[];
	/** Distinct grams, in order of first appearance. */
	unique: string[];
};

export type GramOrigin = 'query' | 'shared' | 'document';

export type NgramComparison = {
	n: number;
	query: NgramScan;
	doc: NgramScan;
	/** A ∪ B: the query's grams in reading order, then the grams only the document has. */
	union: { gram: string; origin: GramOrigin }[];
	/** |A ∩ B| */
	shared: number;
	/** Jaccard index, between 0 and 1. */
	similarity: number;
	/** Per character of `query.padded`: true when a shared gram covers it. */
	queryCover: boolean[];
	/** Per character of `doc.padded`: true when a shared gram covers it. */
	docCover: boolean[];
};

export function scan(text: string, n: number): NgramScan {
	const size = Math.max(1, Math.floor(n));
	const pad = ' '.repeat(size - 1);
	const padded = `${pad}${text.toLowerCase()}${pad}`;
	const windows: GramWindow[] = [];
	for (let start = 0; start + size <= padded.length; start += 1) {
		windows.push({ gram: padded.slice(start, start + size), start });
	}
	const unique: string[] = [];
	const seen = new Set<string>();
	windows.forEach(({ gram }) => {
		if (seen.has(gram)) return;
		seen.add(gram);
		unique.push(gram);
	});
	return { padded, windows, unique };
}

function coverage(source: NgramScan, shared: Set<string>, n: number): boolean[] {
	const cover = source.padded.split('').map(() => false);
	source.windows.forEach(({ gram, start }) => {
		if (!shared.has(gram)) return;
		for (let k = 0; k < n; k += 1) cover[start + k] = true;
	});
	return cover;
}

export function compare(query: string, document: string, n: number): NgramComparison {
	const size = Math.max(1, Math.floor(n));
	const q = scan(query, size);
	const d = scan(document, size);
	const inQuery = new Set(q.unique);
	const inDoc = new Set(d.unique);
	const sharedSet = new Set(q.unique.filter((gram) => inDoc.has(gram)));

	const union = [
		...q.unique.map((gram) => ({ gram, origin: (sharedSet.has(gram) ? 'shared' : 'query') as GramOrigin })),
		...d.unique.filter((gram) => !inQuery.has(gram)).map((gram) => ({ gram, origin: 'document' as GramOrigin })),
	];

	const shared = sharedSet.size;
	return {
		n: size,
		query: q,
		doc: d,
		union,
		shared,
		similarity: union.length ? shared / union.length : 0,
		queryCover: coverage(q, sharedSet, size),
		docCover: coverage(d, sharedSet, size),
	};
}
